import { z } from 'zod';
// ─── Event Types ─────────────────────────────────────────────────────────────
export const createEventTypeSchema = z.object({
    name: z.string().min(1).max(255),
    nameHe: z.string().max(255).optional(),
    description: z.string().max(1000).optional(),
});
export const updateEventTypeSchema = z.object({
    name: z.string().min(1).max(255).optional(),
    nameHe: z.string().max(255).nullable().optional(),
    description: z.string().max(1000).nullable().optional(),
    isActive: z.boolean().optional(),
    sortOrder: z.number().int().optional(),
});
// ─── Vendor Categories ───────────────────────────────────────────────────────
export const createVendorCategorySchema = z.object({
    name: z.string().min(1).max(255),
    nameHe: z.string().max(255).optional(),
});
export const updateVendorCategorySchema = z.object({
    name: z.string().min(1).max(255).optional(),
    nameHe: z.string().max(255).nullable().optional(),
    isActive: z.boolean().optional(),
    sortOrder: z.number().int().optional(),
});
// ─── Expense Statuses ────────────────────────────────────────────────────────
/**
 * One status cannot mean both "a vendor is booked" and "nothing left to pay":
 * booking moves a line onto the first, paying it off onto the second, and a
 * status that was both would never show the line move.
 */
export const EXPENSE_STATUS_BOTH_FLAGS_MESSAGE = 'A status cannot be both the booked status and the paid-in-full status';
function notPaidInFullAndBooked(s) {
    return !(s.isPaidInFull && s.isBooked);
}
const BOTH_FLAGS_ISSUE = { message: EXPENSE_STATUS_BOTH_FLAGS_MESSAGE, path: ['isBooked'] };
/**
 * The booked and paid-in-full lookups read active statuses only, so a flag on
 * an inactive status would be no answer at all — and setting it clears the
 * active holder. A status is activated first (the same request may do both).
 */
export const EXPENSE_STATUS_INACTIVE_FLAG_MESSAGE = 'An inactive status cannot be the booked or paid-in-full status — activate it first';
function notFlaggedWhileDeactivated(s) {
    return !(s.isActive === false && (s.isPaidInFull || s.isBooked));
}
const INACTIVE_FLAG_ISSUE = { message: EXPENSE_STATUS_INACTIVE_FLAG_MESSAGE, path: ['isActive'] };
export const createExpenseStatusSchema = z
    .object({
    name: z.string().min(1).max(255),
    nameHe: z.string().max(255).optional(),
    color: z
        .string()
        .max(7)
        .regex(/^#[0-9A-Fa-f]{6}$/)
        .optional(),
    sortOrder: z.number().int().optional(),
    isPaidInFull: z.boolean().optional(),
    isBooked: z.boolean().optional(),
})
    .refine(notPaidInFullAndBooked, BOTH_FLAGS_ISSUE);
export const updateExpenseStatusSchema = z
    .object({
    name: z.string().min(1).max(255).optional(),
    nameHe: z.string().max(255).nullable().optional(),
    color: z
        .string()
        .max(7)
        .regex(/^#[0-9A-Fa-f]{6}$/)
        .nullable()
        .optional(),
    sortOrder: z.number().int().optional(),
    isActive: z.boolean().optional(),
    isPaidInFull: z.boolean().optional(),
    isBooked: z.boolean().optional(),
})
    .refine(notPaidInFullAndBooked, BOTH_FLAGS_ISSUE)
    .refine(notFlaggedWhileDeactivated, INACTIVE_FLAG_ISSUE);
// ─── Payment Methods ─────────────────────────────────────────────────────────
export const createPaymentMethodSchema = z.object({
    name: z.string().min(1).max(255),
    nameHe: z.string().max(255).optional(),
    isProducerFunded: z.boolean().optional(),
});
export const updatePaymentMethodSchema = z.object({
    name: z.string().min(1).max(255).optional(),
    nameHe: z.string().max(255).nullable().optional(),
    isActive: z.boolean().optional(),
    isProducerFunded: z.boolean().optional(),
});
//# sourceMappingURL=settings.js.map